import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Award, Clock, Leaf } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export function BrandStory() {
  const { t } = useTranslation('common')

  const stats = [
    {
      icon: Clock,
      value: t('home.brandStory.stats.years'),
      label: t('home.brandStory.stats.yearsLabel'),
    },
    {
      icon: Leaf,
      value: t('home.brandStory.stats.products'),
      label: t('home.brandStory.stats.productsLabel'),
    },
    {
      icon: Award,
      value: t('home.brandStory.stats.quality'),
      label: t('home.brandStory.stats.qualityLabel'),
    },
  ]

  return (
    <section className="relative overflow-hidden" aria-labelledby="brand-story-title">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-50" />

      <div className="container-fiorucci relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-20 lg:py-32">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/images/mondo_fiorucci_Mondo-Fiorucci_1440x612-hero-img_DESKTOP.jpg"
                alt={t('home.brandStory.title')}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/40 to-transparent" />
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-6 -right-6 lg:bottom-8 lg:-right-8 bg-[#DF631E] text-white p-6 rounded-2xl shadow-xl"
            >
              <div className="text-center">
                <div className="text-4xl font-serif font-bold">170+</div>
                <div className="text-sm opacity-90">{t('home.brandStory.stats.yearsLabel')}</div>
              </div>
            </motion.div>

            {/* Decorative Frame */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-l-4 border-t-4 border-[#DF631E] rounded-tl-3xl" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-4 border-b-4 border-[#DF631E] rounded-br-3xl" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Section Label */}
            <span className="inline-block px-4 py-1.5 bg-[#4E2118]/10 text-[#4E2118] text-sm font-semibold rounded-full mb-6">
              {t('nav.about')}
            </span>

            {/* Title */}
            <h2 id="brand-story-title" className="heading-section text-[#4E2118] mb-6">
              {t('home.brandStory.title')}
            </h2>

            {/* Subtitle */}
            <p className="text-xl text-[#DF631E] font-serif italic mb-6">
              {t('home.brandStory.subtitle')}
            </p>

            {/* Description */}
            <p className="text-body-lg text-[#4E2118]/80 mb-8 leading-relaxed">
              {t('home.brandStory.description')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-10 py-6 border-y border-[#F3E4D6]">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="text-center"
                >
                  <stat.icon className="w-6 h-6 mx-auto mb-2 text-[#DF631E]" />
                  <div className="text-lg font-bold text-[#4E2118]">{stat.value}</div>
                  <div className="text-xs text-[#4E2118]/60 uppercase tracking-wide">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <LocalizedLink
              to="about"
              className="btn-primary inline-flex group"
            >
              {t('home.brandStory.cta')}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </LocalizedLink>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
